import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userApi } from "@bw/shared/api";
import type {
  UserProfileResponse,
  UserProfileUpdate,
} from "@bw/shared/types/generated";
import { profileQueryKey } from "./useMyProfile";

/**
 * useUpdateProfile (player Me slice).
 *
 * A React Query mutation over userApi.updateProfile (PATCH /users/profile). On
 * success it:
 *   - returns the fresh UserProfileResponse
 *   - invalidates `profileQueryKey` so the next getMyProfile read (and the Me
 *     overview) reflects the update.
 *
 * It lives in `common/query` alongside `useMyProfile` (the single profile READ)
 * so it is the SINGLE profile-WRITE surface and invalidates the SAME key the
 * read uses — mirroring onboarding's useCreateProfile.
 *
 * It surfaces the raw ApiError (404 no-profile, 422 validation) on
 * `mutation.error` WITHOUT throwing to an error boundary — the page renders the
 * message inline. No business logic here: the body is exactly a partial
 * UserProfileUpdate.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation<UserProfileResponse, unknown, UserProfileUpdate>({
    // `userApi.updateProfile` is now typed against the GENERATED
    // UserProfileResponse (#1311) — account_status / requires_parental_consent are
    // REQUIRED at the boundary, so no narrowing cast is needed (mirrors
    // useCreateProfile / useMyProfile).
    mutationFn: (data: UserProfileUpdate) => userApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileQueryKey });
    },
  });
}
